/**
 * State/Province lists aligned with mango-api enum **declaration order** (not A–Z).
 *
 * - **`phoneNumber`** — `PhoneNumber.StateOrProvince` only (65 codes: US incl. DC & Puerto Rico,
 *   plus Canada). Does **not** include U.S. territories AS, GU, MP, VI — those are not valid
 *   `e911_state` choices on the phone number model.
 * - **`company`** — `Company.StateOrProvince` (69 codes): US + DC (no PR in the US block),
 *   then **U.S. territories** (American Samoa, Guam, Northern Mariana Islands, Puerto Rico,
 *   Virgin Islands), then Canada.
 */

export interface StateOrProvinceOption {
  value: string
  label: string
}

/** US + DC + PR — order matches `PhoneNumber.StateOrProvince` (United States block). */
export const PHONE_NUMBER_US_OPTIONS: StateOrProvinceOption[] = [
  { value: 'AL', label: 'Alabama' },
  { value: 'AK', label: 'Alaska' },
  { value: 'AZ', label: 'Arizona' },
  { value: 'AR', label: 'Arkansas' },
  { value: 'CA', label: 'California' },
  { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' },
  { value: 'DE', label: 'Delaware' },
  { value: 'DC', label: 'District of Columbia' },
  { value: 'FL', label: 'Florida' },
  { value: 'GA', label: 'Georgia' },
  { value: 'ID', label: 'Idaho' },
  { value: 'IL', label: 'Illinois' },
  { value: 'IN', label: 'Indiana' },
  { value: 'IA', label: 'Iowa' },
  { value: 'HI', label: 'Hawaii' },
  { value: 'KS', label: 'Kansas' },
  { value: 'KY', label: 'Kentucky' },
  { value: 'LA', label: 'Louisiana' },
  { value: 'ME', label: 'Maine' },
  { value: 'MD', label: 'Maryland' },
  { value: 'MA', label: 'Massachusetts' },
  { value: 'MI', label: 'Michigan' },
  { value: 'MN', label: 'Minnesota' },
  { value: 'MS', label: 'Mississippi' },
  { value: 'MO', label: 'Missouri' },
  { value: 'MT', label: 'Montana' },
  { value: 'NE', label: 'Nebraska' },
  { value: 'NV', label: 'Nevada' },
  { value: 'NH', label: 'New Hampshire' },
  { value: 'NJ', label: 'New Jersey' },
  { value: 'NM', label: 'New Mexico' },
  { value: 'NY', label: 'New York' },
  { value: 'NC', label: 'North Carolina' },
  { value: 'ND', label: 'North Dakota' },
  { value: 'OH', label: 'Ohio' },
  { value: 'OK', label: 'Oklahoma' },
  { value: 'OR', label: 'Oregon' },
  { value: 'PA', label: 'Pennsylvania' },
  { value: 'PR', label: 'Puerto Rico' },
  { value: 'RI', label: 'Rhode Island' },
  { value: 'SC', label: 'South Carolina' },
  { value: 'SD', label: 'South Dakota' },
  { value: 'TN', label: 'Tennessee' },
  { value: 'TX', label: 'Texas' },
  { value: 'UT', label: 'Utah' },
  { value: 'VT', label: 'Vermont' },
  { value: 'VA', label: 'Virginia' },
  { value: 'WA', label: 'Washington' },
  { value: 'WV', label: 'West Virginia' },
  { value: 'WI', label: 'Wisconsin' },
  { value: 'WY', label: 'Wyoming' },
]

/** Canadian provinces/territories — order matches both models’ Canada block. */
export const CANADA_STATE_PROVINCE_OPTIONS: StateOrProvinceOption[] = [
  { value: 'AB', label: 'Alberta' },
  { value: 'BC', label: 'British Columbia' },
  { value: 'MB', label: 'Manitoba' },
  { value: 'NB', label: 'New Brunswick' },
  { value: 'NL', label: 'Newfoundland and Labrador' },
  { value: 'NT', label: 'Northwest Territories' },
  { value: 'NS', label: 'Nova Scotia' },
  { value: 'NU', label: 'Nunavut' },
  { value: 'ON', label: 'Ontario' },
  { value: 'PE', label: 'Prince Edward Island' },
  { value: 'QC', label: 'Quebec' },
  { value: 'SK', label: 'Saskatchewan' },
  { value: 'YT', label: 'Yukon' },
]

/** U.S. territories — `Company.StateOrProvince` block (after US states + DC). */
export const COMPANY_US_TERRITORY_OPTIONS: StateOrProvinceOption[] = [
  { value: 'AS', label: 'American Samoa' },
  { value: 'GU', label: 'Guam' },
  { value: 'MP', label: 'Northern Mariana Islands' },
  { value: 'PR', label: 'Puerto Rico' },
  { value: 'VI', label: 'Virgin Islands' },
]

/** US + DC only — same order as Company model (Puerto Rico is not in this block). */
export const COMPANY_US_OPTIONS: StateOrProvinceOption[] =
  PHONE_NUMBER_US_OPTIONS.filter((o) => o.value !== 'PR')

/** Flat list for phone numbers / E911 — `PhoneNumber.StateOrProvince` order. */
export const PHONE_NUMBER_STATE_PROVINCE_OPTIONS: StateOrProvinceOption[] = [
  ...PHONE_NUMBER_US_OPTIONS,
  ...CANADA_STATE_PROVINCE_OPTIONS,
]

/** Flat list for company addresses — `Company.StateOrProvince` order. */
export const COMPANY_STATE_PROVINCE_OPTIONS: StateOrProvinceOption[] = [
  ...COMPANY_US_OPTIONS,
  ...COMPANY_US_TERRITORY_OPTIONS,
  ...CANADA_STATE_PROVINCE_OPTIONS,
]

export type StateProvinceVariant = 'phoneNumber' | 'company'

export interface StateProvinceSection {
  id: string
  label: string
  items: StateOrProvinceOption[]
}

export function getStateProvinceSections(
  variant: StateProvinceVariant,
  labels: {
    unitedStates: string
    usTerritories: string
    canada: string
  },
): StateProvinceSection[] {
  if (variant === 'phoneNumber') {
    return [
      {
        id: 'mango-sp-phone-us',
        label: labels.unitedStates,
        items: PHONE_NUMBER_US_OPTIONS,
      },
      {
        id: 'mango-sp-phone-ca',
        label: labels.canada,
        items: CANADA_STATE_PROVINCE_OPTIONS,
      },
    ]
  }
  return [
    {
      id: 'mango-sp-co-us',
      label: labels.unitedStates,
      items: COMPANY_US_OPTIONS,
    },
    {
      id: 'mango-sp-co-ter',
      label: labels.usTerritories,
      items: COMPANY_US_TERRITORY_OPTIONS,
    },
    {
      id: 'mango-sp-co-ca',
      label: labels.canada,
      items: CANADA_STATE_PROVINCE_OPTIONS,
    },
  ]
}

export function getStateProvinceFlatOptions(
  variant: StateProvinceVariant,
): StateOrProvinceOption[] {
  return variant === 'phoneNumber'
    ? PHONE_NUMBER_STATE_PROVINCE_OPTIONS
    : COMPANY_STATE_PROVINCE_OPTIONS
}
